import {useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect} from 'react';
import {useSearchParams} from 'react-router';
import _ from 'lodash';
import {str2type, type2str} from './util.js';

export const useObjectMemo = (obj={})=>useMemo(()=>obj, Object.values(obj));

export const useOnce = (fn, condition=true)=>{
    let initialized = useRef(false);
    useEffect(()=>{
        if (!condition || initialized.current)
            return;
        initialized.current = true;
        fn();
    }, [condition, initialized.current]);
};

export const useBusyCallback = (fn, deps=[])=>{
    let _fn = useCallback(fn, deps);
    let [busy, setBusy] = useState(false);
    let run = useCallback(async (...args)=>{
        if (busy)
            return;
        setBusy(true);
        try { return await _fn(...args); }
        finally { setBusy(false); }
    }, [_fn]);
    return [run, busy];
};

export const useWindowSize = ()=>{
    let [size, setSize] = useState([0, 0]);
    useLayoutEffect(()=>{
        let updateSize = ()=>setSize([window.innerWidth, window.innerHeight]);
        window.addEventListener('resize', updateSize);
        updateSize();
        return ()=>window.removeEventListener('resize', updateSize);
    }, []);
    return size;
};

// opt => https://api.reactrouter.com/v7/interfaces/react_router.NavigateOptions.html
export const useQueryParam = (k, opt)=>{
    let [params, setParams] = useSearchParams();
    let [param, setParam] = useState();
    useEffect(()=>{ setParam(params.get(k)); }, [params, k]);
    let _setParam = useCallback(v=>{
        setParam(v);
        setParams(prev=>_.omitBy({...prev, [k]: v}, _.isNil), opt);
    }, [k, opt]);
    return [param, _setParam];
};

export const useStorageItem = (k, type=String, initial)=>{
    let [value, setValue] = useState(initial);
    let _get = useCallback(()=>str2type(localStorage.getItem(k), type), [k, type]);
    let _set = useCallback(v=>{
        if (v===undefined) {
            setValue();
            localStorage.removeItem(k);
            return;
        }
        setValue(prev=>{
            let newValue = _.isFunction(v) ? v(prev) : v;
            localStorage.setItem(k, type2str(v));
            return newValue;
        });
    }, [k]);
    useEffect(()=>{ setValue(_get()); }, [_get]);
    useEffect(()=>{
        let listener = ()=>setValue(_get());
        window.addEventListener('storage', listener);
        return ()=>window.removeEventListener('storage', listener);
    }, [_get]);
    return [value, _set];
};

export const useLiveRef = v=>{
    let ref = useRef(v);
    useEffect(()=>{ ref.current = v; }, [v]);
    return ref;
};

export const useHotkey = (key, cb, enabled=true)=>{
    let cbRef = useLiveRef(cb);
    useEffect(()=>{
        if (!key||!enabled)
            return;
        let listener = ev=>{
            if (ev.code==key)
                cbRef.current?.();
        };
        window.addEventListener('keydown', listener);
        return ()=>window.removeEventListener('keydown', listener);
    }, [key, enabled]);
};
